import {Component, Input, OnChanges, OnInit} from '@angular/core';

import {EMPTY, merge, Observable, of, Subject} from 'rxjs';
import {finalize, map, switchMap, takeUntil, tap} from 'rxjs/operators';

import {Destroyable} from '../../../destroyable.component';
import {DefaultPagination, Pagination} from '../../ui';
import {User, UserService} from '../../user';
import {prettyJson} from '../../utils/json';
import {PersonPayrollLogDTO, SdcEventTypes} from '../sdconnect.model';
import {SdconnectService} from '../sdconnect.service';

@Component({
  selector: 'gp-person-payroll-logs-list',
  templateUrl: './person-payroll-logs-list.component.html',
  styleUrls: ['./person-payroll-logs-list.component.scss']
})
export class PersonPayrollLogsListComponent extends Destroyable implements OnInit, OnChanges {

  @Input()
  public contractId: string;

  @Input()
  public sdcLogId: string;

  @Input()
  public sdcLogType: string;

  @Input()
  public latestOnly = false;

  public timezone: string;

  public personPayrollLogs$: Observable<PersonPayrollLogDTO[]>;

  public pagination: Pagination = new DefaultPagination(9);

  public loading = false;

  public isCopied = false;

  private reload$ = new Subject<boolean>();

  constructor(private userService: UserService,
              private sdconnectService: SdconnectService) {
    super();
  }

  public ngOnInit() {
    this.userService.getUser()
      .pipe(
        tap((user: User) => this.timezone = user.timezone),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();

    this.personPayrollLogs$ = merge(this.pagination.pageChanged$.asObservable(), this.reload$)
      .pipe(
        switchMap(() => this.getPersonPayrollLogs()),
        takeUntil(this.componentDestroyed$)
      );
  }

  public ngOnChanges() {
    this.reload();
  }

  public reload() {
    this.reload$.next(true);
    this.pagination.first();
  }

  public toggleTooltip(event: MouseEvent, isCopied: boolean): void {
    event.stopPropagation();
    this.isCopied = isCopied;
  }

  public emphasized(personLog: PersonPayrollLogDTO): boolean {
    return !this.latestOnly && (personLog.isLatest || personLog.latest);
  }

  public get noLogsMessageKey(): string {
    return this.sdcLogType === SdcEventTypes.SDC3_PAYROLL_INPUT ?
      'SDC.PAYROLL.LOG.Missing' : 'SDC.PERSON.LOG.Missing';
  }

  private getPersonPayrollLogs(): Observable<PersonPayrollLogDTO[]> {
    if (!this.contractId && !this.sdcLogId) {
      return of([]);
    }
    this.loading = true;
    const latest = this.latestOnly ? true : undefined;
    return this.getPersonOrPayrollLogs(latest)
      .pipe(
        tap((result: { total: number, data: PersonPayrollLogDTO[] }) => {
          this.pagination.update(result.total);
        }),
        map((result: { total: number, data: PersonPayrollLogDTO[] }) => {
          return result.data;
        }),
        finalize(() => {
          this.loading = false;
        })
      );
  }

  private getPersonOrPayrollLogs(latest: boolean) {
    return this.sdcLogType === SdcEventTypes.SDC3 ?
      this.getPersonPayrollLogsByPath('person-logs', latest) : this.sdcLogType === SdcEventTypes.SDC3_PAYROLL_INPUT ?
        this.getPersonPayrollLogsByPath('payroll-logs', latest) : EMPTY;
  }

  private getPersonPayrollLogsByPath(path: string, latest: boolean) {
    return this.contractId ?
      this.sdconnectService.getPersonPayrollLogsByContractId(
        path,
        this.contractId,
        latest,
        this.pagination.perPage,
        this.pagination.currentPage.number) :
      this.sdconnectService.getPersonPayrollLogsBySdcLogId(
        path,
        this.sdcLogId,
        this.pagination.perPage,
        this.pagination.currentPage.number);
  }

  public getPrettyJson(data: string): string {
    return prettyJson(data || '{}');
  }
}
